<div
  class="flex flex-col gap-6 max-[767px]:gap-4"
  [ngClass]="
    hasPending() || canDownload()
      ? 'pb-[calc(6.5rem+env(safe-area-inset-bottom))]'
      : 'max-[767px]:pb-2'
  "
>
  <section
    class="flex flex-col gap-5 rounded-3xl border border-[color:var(--border-subtle)] bg-white p-7 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.06)] max-[767px]:gap-4 max-[767px]:rounded-2xl max-[767px]:p-4"
  >
    <!-- Header: titolo + azioni -->
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2
          class="font-display text-2xl leading-tight text-gray-900 sm:text-3xl md:text-4xl"
        >
          Genera le immagini AI
        </h2>
        <p class="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
          Genera tutte le foto in un colpo solo, oppure apri le impostazioni
          avanzate di ogni foto per indicare la stanza, gli arredi e istruzioni
          su misura. Il pacchetto da scaricare comparirà in basso appena pronto.
        </p>
      </div>
      <div class="flex shrink-0 flex-wrap gap-2">
        <button
          type="button"
          class="btn-outline-gray whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
          [disabled]="isGenerating()"
          (click)="back.emit()"
        >
          Torna allo stile
        </button>
        <button
          type="button"
          class="btn-outline-gray whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
          [disabled]="isGenerating()"
          (click)="restart.emit()"
        >
          Ricomincia
        </button>
      </div>
    </div>

    <!-- Pannello impostazioni: confronto + logo agenzia -->
    <div
      class="flex flex-col gap-3 rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-alt)] p-4 max-[767px]:gap-2.5 max-[767px]:p-3"
    >
      <span
        class="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-[color:var(--ink-muted)]"
        >Impostazioni</span
      >

      <!-- Toggle globale per la modalità di confronto -->
      <div class="flex flex-wrap items-center gap-3">
        <span class="text-sm font-semibold text-[color:var(--ink-strong)]"
          >Confronto</span
        >
        <div
          class="flex flex-nowrap gap-0 rounded-[0.55rem] bg-white p-[0.2rem] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
          role="group"
          aria-label="Modalità di confronto globale"
        >
          @for (mode of previewModes(); track mode.value) {
            <button
              type="button"
              class="rounded-[0.35rem] bg-transparent px-2.5 py-1 text-[0.72rem] font-semibold transition-colors max-[767px]:px-2"
              [ngClass]="
                previewMode() === mode.value
                  ? 'bg-[color:var(--surface-alt)] text-[color:var(--ink-strong)] shadow-[0_1px_2px_rgba(0,0,0,0.1)]'
                  : 'text-[color:var(--ink-muted)] hover:text-[color:var(--ink-strong)]'
              "
              (click)="setGlobalPreviewMode(mode.value)"
            >
              {{ mode.label }}
            </button>
          }
        </div>
      </div>

      @if (showCompanyOption()) {
        <div
          class="border-t border-[color:var(--border-subtle)] pt-3 max-[767px]:pt-2.5"
        >
          <label
            class="flex cursor-pointer items-start gap-3 transition-opacity hover:opacity-80"
          >
            <input
              type="checkbox"
              class="mt-0.5 h-4 w-4 shrink-0 accent-black"
              [checked]="addCompanyLogo()"
              (change)="onToggleCompanyLogo($event)"
            />
            <span class="flex flex-col gap-0.5">
              <strong
                class="text-sm font-semibold text-[color:var(--ink-strong)]"
                >Applica logo agenzia</strong
              >
              <small
                class="text-xs leading-relaxed text-[color:var(--ink-soft)]"
                >Aggiunge il logo della tua agenzia come watermark sulle foto
                generate.</small
              >
            </span>
          </label>
        </div>
      }
    </div>
  </section>

  @if (limitWarning()) {
    <casa-doc-alert
      type="warning"
      title="Limite raggiunto"
      [message]="limitWarning() ?? ''"
    ></casa-doc-alert>
  }

  <section
    class="flex flex-col gap-6 rounded-3xl border border-[color:var(--border-subtle)] bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.06)] min-[1100px]:p-6 max-[767px]:gap-4 max-[767px]:border-0 max-[767px]:bg-transparent max-[767px]:p-0 max-[767px]:shadow-none"
  >
    @for (img of processedImages(); track img.index) {
      <article
        class="overflow-hidden rounded-2xl border border-[color:var(--border-subtle)] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.05)] max-[767px]:rounded-[0.9rem]"
      >
        <!-- Header accordion: miniatura + nome + stato; toggle del corpo foto -->
        <button
          type="button"
          class="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 max-[767px]:px-3"
          [attr.aria-expanded]="!isCardCollapsed(img)"
          (click)="toggleCard(img)"
        >
          <img
            class="h-12 w-16 shrink-0 rounded-lg bg-gray-100 object-cover max-[767px]:h-10 max-[767px]:w-14"
            [src]="img.aiUrl || img.originalUrl"
            alt=""
          />
          <span class="min-w-0 flex-1">
            <span
              class="block truncate text-sm font-semibold text-[color:var(--ink-strong)]"
              >{{ img.originalFile.name }}</span
            >
            <span
              class="mt-0.5 inline-flex items-center gap-1.5 text-xs font-medium"
              [ngClass]="{
                'text-emerald-600': img.status === 'done',
                'text-blue-casa-doc': img.status === 'processing',
                'text-red-600': img.status === 'error',
                'text-gray-400': img.status === 'pending',
              }"
            >
              @if (img.status === 'processing') {
                <span
                  class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                  aria-hidden="true"
                ></span>
              }
              {{ statusLabel(img.status) }}
            </span>
          </span>
          <svg
            class="h-5 w-5 shrink-0 text-gray-400 transition-transform"
            [class.rotate-180]="!isCardCollapsed(img)"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        @if (!isCardCollapsed(img)) {
          <!-- Selettore confronto per-foto (solo quando la foto è generata) -->
          @if (img.status === 'done' && img.aiUrl) {
            <div
              class="flex items-center justify-between gap-3 border-b border-[color:var(--border-subtle)] px-4 py-2.5 max-[767px]:px-3 max-[767px]:py-2"
            >
              <span class="results-shell__eyebrow">Confronto</span>
              <div
                class="flex flex-nowrap gap-0 rounded-[0.55rem] bg-[color:var(--surface-alt)] p-[0.2rem]"
                role="group"
                aria-label="Modalità di confronto foto"
              >
                @for (mode of previewModes(); track mode.value) {
                  <button
                    type="button"
                    class="rounded-[0.35rem] bg-transparent px-2.5 py-1 text-[0.72rem] font-semibold transition-colors max-[767px]:px-2"
                    [ngClass]="
                      previewModeFor(img.index) === mode.value
                        ? 'bg-white text-[color:var(--ink-strong)] shadow-[0_1px_2px_rgba(0,0,0,0.1)]'
                        : 'text-[color:var(--ink-muted)] hover:text-[color:var(--ink-strong)]'
                    "
                    (click)="setImagePreviewMode(img.index, mode.value)"
                  >
                    {{ mode.label }}
                  </button>
                }
              </div>
            </div>
          }

          <div
            class="flex min-h-[72vh] items-center justify-center bg-[#0a0a0a] p-[0.9rem] max-[767px]:min-h-[min(58vh,30rem)] max-[767px]:p-[0.45rem]"
          >
            @if (img.status === 'done' && img.aiUrl) {
              <casa-doc-ab-comparison
                class="block w-full"
                [originalUrl]="img.originalUrl"
                [aiUrl]="img.aiUrl"
                [mode]="previewModeFor(img.index)"
              ></casa-doc-ab-comparison>
            } @else if (img.status === 'processing') {
              <div
                class="relative flex min-h-[72vh] w-full flex-col items-center justify-center gap-4 bg-[#0a0a0a] p-4 text-center text-[color:var(--ink-on-dark)] max-[767px]:min-h-[min(58vh,30rem)]"
              >
                <img
                  class="block w-full max-h-[70vh] rounded-lg object-contain"
                  [src]="img.originalUrl"
                  [alt]="img.originalFile.name"
                />
                <div
                  class="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 backdrop-blur-[4px] max-[767px]:rounded-[0.35rem]"
                >
                  <span
                    class="h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-white/90 max-[767px]:h-8 max-[767px]:w-8"
                  ></span>
                </div>
              </div>
            } @else if (img.status === 'error') {
              <div
                class="flex min-h-[72vh] w-full flex-col items-center justify-center gap-4 bg-[#0a0a0a] p-8 text-center text-[color:var(--ink-on-dark)] max-[767px]:min-h-[min(58vh,30rem)] max-[767px]:p-5"
              >
                <casa-doc-alert
                  type="danger"
                  title="Errore"
                  [message]="img.errorMessage || 'Errore di elaborazione'"
                ></casa-doc-alert>
              </div>
            } @else {
              <div
                class="flex min-h-[72vh] w-full flex-col items-center justify-center gap-4 bg-[#0a0a0a] p-4 text-center text-[color:var(--ink-on-dark)] max-[767px]:min-h-[min(58vh,30rem)]"
              >
                <img
                  class="block w-full max-h-[70vh] rounded-lg object-contain"
                  [src]="img.originalUrl"
                  [alt]="img.originalFile.name"
                />
              </div>
            }
          </div>

          @if (!isExpanded(img.index)) {
            <!-- Modalità base: due percorsi chiari → genera subito oppure personalizza -->
            <div
              class="flex flex-wrap items-stretch gap-2 px-4 py-3 max-[767px]:flex-col"
            >
              <button
                type="button"
                class="btn-solid-blue-casa-doc grow whitespace-nowrap basis-0 min-[480px]:min-w-[10rem]"
                [disabled]="
                  img.status === 'processing' ||
                  quotaExhausted() ||
                  isGenerating()
                "
                (click)="generateOne.emit(img.index)"
              >
                {{ img.status === 'done' ? 'Rigenera foto' : 'Genera foto' }}
              </button>
              <button
                type="button"
                aria-expanded="false"
                [disabled]="img.status === 'processing'"
                (click)="toggleAdvanced(img.index)"
                class="inline-flex grow basis-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-blue-casa-doc/40 bg-blue-casa-doc/5 px-3.5 py-2.5 text-sm font-semibold text-blue-casa-doc transition-colors hover:bg-blue-casa-doc/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-casa-doc/30 min-[480px]:min-w-[10rem] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-casa-doc/5"
              >
                <svg
                  class="h-4 w-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  aria-hidden="true"
                >
                  <line x1="3" y1="8" x2="13" y2="8" />
                  <line x1="18" y1="8" x2="21" y2="8" />
                  <circle cx="15.5" cy="8" r="2.2" />
                  <line x1="3" y1="16" x2="8" y2="16" />
                  <line x1="13" y1="16" x2="21" y2="16" />
                  <circle cx="10.5" cy="16" r="2.2" />
                </svg>
                Personalizza foto
              </button>
            </div>
          } @else {
            <!-- Modalità avanzata: ritorno alla base esplicito + titolo cambiato -->
            <button
              type="button"
              aria-expanded="true"
              (click)="toggleAdvanced(img.index)"
              class="flex w-full items-center justify-center gap-2 border-y border-blue-casa-doc/20 bg-blue-casa-doc/5 px-4 py-3 text-sm font-bold text-blue-casa-doc transition-colors hover:bg-blue-casa-doc/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-casa-doc/30"
            >
              <svg
                class="h-4 w-4 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Torna alla modalità base
            </button>

            <div
              class="flex items-center gap-2 bg-gray-50/80 px-4 pt-4 sm:px-5"
            >
              <span class="results-shell__eyebrow text-blue-casa-doc"
                >Modalità avanzata</span
              >
              <span class="text-[0.72rem] text-gray-400"
                >personalizzi solo questa foto</span
              >
            </div>

            <casa-doc-photo-advanced-panel
              [config]="img.config"
              (configChange)="
                configChange.emit({ index: img.index, config: $event })
              "
            ></casa-doc-photo-advanced-panel>
            <div class="px-[1.1rem] pb-4">
              <button
                type="button"
                class="btn-solid-blue-casa-doc w-full"
                [disabled]="
                  img.status === 'processing' ||
                  quotaExhausted() ||
                  isGenerating()
                "
                (click)="generateOne.emit(img.index)"
              >
                {{
                  img.status === 'done'
                    ? 'Rigenera con queste impostazioni'
                    : 'Genera con queste impostazioni'
                }}
              </button>
            </div>
          }

          @if (showPromptDebug()) {
            <details
              open
              class="mt-3 overflow-hidden rounded-xl border border-fuchsia-300 bg-fuchsia-50/70"
            >
              <summary
                class="cursor-pointer select-none px-3 py-2 text-[0.7rem] font-bold uppercase tracking-wider text-fuchsia-700"
              >
                🔎 Debug prompt nanobanana · anteprima live
              </summary>
              <pre
                class="m-0 max-h-80 overflow-auto whitespace-pre-wrap break-words border-t border-fuchsia-200 bg-white/80 px-3 py-2.5 font-mono text-[0.7rem] leading-relaxed text-gray-800"
                >{{ promptPreview(img.index) }}</pre
              >
            </details>
          }

          @if (img.status === 'done') {
            <div
              class="flex flex-col gap-3 border-t border-[color:var(--border-subtle)] bg-white p-[1rem_1.1rem] max-[767px]:p-[0.75rem_0.85rem]"
            >
              <p
                class="m-0 text-[0.82rem] font-semibold text-[color:var(--ink-strong)]"
              >
                Vuoi modificare ancora l'immagine?
              </p>
              <textarea
                class="w-full resize-y rounded-xl border border-[color:var(--border-subtle)] bg-[var(--surface-alt)] px-[0.9rem] py-3 text-sm leading-[1.6] text-[color:var(--ink-strong)] placeholder:text-[color:var(--ink-muted)] focus:border-[color:var(--brand-primary)] focus:shadow-[0_0_0_3px_var(--brand-soft)] focus:outline-none"
                placeholder="Descrivi la modifica: es. 'aggiungi una pianta in angolo' oppure 'rendi la luce più calda'..."
                rows="3"
                [value]="editPrompt(img.index)"
                (input)="setEditPrompt(img.index, $any($event.target).value)"
              ></textarea>
              <div
                class="flex flex-wrap gap-2 max-[767px]:grid max-[767px]:grid-cols-1 max-[767px]:[&>button]:min-h-[2.75rem] max-[767px]:[&>button]:w-full"
              >
                <button
                  type="button"
                  class="btn-solid-blue-casa-doc"
                  [disabled]="
                    !editPrompt(img.index).trim() ||
                    quotaExhausted() ||
                    isGenerating()
                  "
                  (click)="submitEdit(img.index)"
                >
                  Genera
                </button>
              </div>
            </div>
          }
        }
      </article>
    }
  </section>
</div>

<!-- Sticky footer: durante la generazione mostra solo il progresso; a fine
     elaborazione compare il download. "Genera tutte" sparisce dopo il click. -->
@if (
  isGenerating() || canDownload() || (!processAllClicked() && hasPending())
) {
  <div
    class="fixed inset-x-0 bottom-0 z-30 border-t border-[color:var(--border-subtle)] bg-white/95 shadow-[0_-8px_32px_rgba(0,0,0,0.10)] backdrop-blur-md"
  >
    <div
      class="mx-auto flex w-full max-w-[80rem] items-center justify-between gap-3 px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] max-[767px]:px-4"
    >
      @if (isGenerating()) {
        <div
          class="flex w-full items-center justify-center gap-3 text-sm font-semibold text-[color:var(--ink-strong)]"
        >
          <span
            class="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-casa-doc/30 border-t-blue-casa-doc"
            aria-hidden="true"
          ></span>
          <span aria-live="polite">Generazione in corso…</span>
        </div>
      } @else {
        <div
          class="flex items-center gap-3 max-[767px]:flex-wrap max-[767px]:justify-end"
        >
          @if (showCompanyOption()) {
            <label
              class="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-medium transition-colors hover:bg-gray-100 max-[767px]:px-1.5"
            >
              <input
                type="checkbox"
                class="h-3.5 w-3.5 shrink-0 accent-black"
                [checked]="addCompanyLogo()"
                (change)="onToggleCompanyLogo($event)"
              />
              <span class="text-[color:var(--ink-strong)]"
                >Applica logo agenzia</span
              >
            </label>
          }
          @if (canDownload()) {
            <button
              type="button"
              class="btn-solid-blue-casa-doc shrink-0 whitespace-nowrap"
              (click)="download.emit()"
            >
              <svg
                class="-ml-0.5 mr-2 h-4 w-4 shrink-0 max-[479px]:hidden"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5M12 15V3" />
              </svg>
              <span class="max-[479px]:hidden">Scarica il pacchetto</span>
              <span class="min-[480px]:hidden">Scarica</span>
              <span
                class="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-bold"
                >{{ doneCount() }}</span
              >
            </button>
          }
          @if (!processAllClicked() && hasPending()) {
            <button
              type="button"
              class="btn-solid-blue-casa-doc shrink-0 whitespace-nowrap"
              [disabled]="quotaExhausted()"
              (click)="triggerProcessAll()"
            >
              <svg
                class="-ml-0.5 mr-2 h-4 w-4 shrink-0 max-[479px]:hidden"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 3v4M3 5h4M6 17v4M4 19h4" />
                <path
                  d="M13 4l2.5 6.5L22 13l-6.5 2.5L13 22l-2.5-6.5L4 13l6.5-2.5z"
                />
              </svg>
              <span class="max-[479px]:hidden">Genera tutte le foto</span>
              <span class="min-[480px]:hidden">Genera tutte</span>
              <span
                class="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs font-bold"
                >{{ pendingCount() }}</span
              >
            </button>
          }
        </div>
      }
    </div>
  </div>
}
